// src/Pages/Invoices/invoicecomps/bcc/BBCSplitIdentityGate.jsx
//
// Renders a verification wall before the BBC Split invoice content is shown.
//
// Rules
// ─────
// 1. If no userInfo → show "Login to view" screen
// 2. If userInfo but email doesn't match invoice.client_email → show "Wrong account" screen
// 3. If userInfo.email matches invoice.client_email (case-insensitive) → render children
// 4. Blitz admins (userInfo.is_admin) bypass the gate entirely

import React, { useState } from 'react';
import {
  Box, Paper, Typography, Avatar, Button, Stack, Alert,
  CircularProgress, Divider,
} from '@mui/material';
import {
  Lock as LockIcon,
  Bolt as BoltIcon,
  PersonOff as PersonOffIcon,
  Logout as LogoutIcon,
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { useNavigate, useLocation } from 'react-router-dom';

const Y      = '#FFD600';
const Y_INK  = '#1A1200';
const Y_DEEP = '#8C6D00';
const Y_TINT = alpha('#FFD600', 0.06);
const Y_BDR  = alpha('#FFD600', 0.22);

// ── Shared layout shell ───────────────────────────────────────────── //
const GateShell = ({ icon, title, subtitle, children }) => (
  <Box
    sx={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: '#f8f9fb',
      px: 2,
    }}
  >
    <Paper
      elevation={0}
      sx={{
        width: '100%',
        maxWidth: 420,
        borderRadius: 4,
        border: '1px solid',
        borderColor: 'divider',
        p: { xs: 3, sm: 4.5 },
        position: 'relative',
        overflow: 'hidden',
        background: 'white',
      }}
    >
      {/* Yellow top accent */}
      <Box
        sx={{
          position: 'absolute',
          top: 0, left: 0, right: 0,
          height: 4,
          background: `linear-gradient(90deg, ${Y} 0%, ${alpha(Y, 0.3)} 100%)`,
        }}
      />

      {/* Blitz wordmark */}
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 3.5 }}>
        <Avatar sx={{ width: 28, height: 28, bgcolor: Y, color: Y_INK }}>
          <BoltIcon sx={{ fontSize: 16 }} />
        </Avatar>
        <Typography
          variant="caption"
          sx={{
            fontWeight: 800,
            color: Y_DEEP,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            fontSize: '0.65rem',
          }}
        >
          Blitz Split
        </Typography>
      </Stack>

      {/* Icon */}
      <Avatar
        sx={{
          width: 52, height: 52,
          bgcolor: Y_TINT,
          border: `1.5px solid ${Y_BDR}`,
          color: Y_DEEP,
          mb: 2,
        }}
      >
        {icon}
      </Avatar>

      {/* Copy */}
      <Typography variant="h6" fontWeight={800} sx={{ mb: 0.75, color: '#1e293b', lineHeight: 1.2 }}>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.65 }}>
        {subtitle}
      </Typography>

      <Divider sx={{ mb: 3 }} />

      {children}
    </Paper>
  </Box>
);

// ── Not logged in ──────────────────────────────────────────────────── //
const LoginWall = ({ location, navigate, invoiceRef }) => (
  <GateShell
    icon={<LockIcon />}
    title="Sign in to view this invoice"
    subtitle={
      invoiceRef
        ? `Invoice ${invoiceRef} is restricted to the billed recipient. Please sign in to continue.`
        : 'This invoice is restricted to the billed recipient. Please sign in to continue.'
    }
  >
    <Stack spacing={1.25}>
      <Button
        fullWidth
        variant="contained"
        size="large"
        onClick={() =>
          navigate('/login', {
            state: { returnTo: location.pathname + location.search },
          })
        }
        sx={{
          bgcolor: Y,
          color: Y_INK,
          fontWeight: 800,
          borderRadius: 2.5,
          textTransform: 'none',
          py: 1.4,
          fontSize: '0.9rem',
          '&:hover': { bgcolor: '#F5C800' },
        }}
      >
        Sign in
      </Button>
      <Button
        fullWidth
        variant="outlined"
        size="large"
        onClick={() =>
          navigate('/onboard', {
            state: { returnTo: location.pathname + location.search },
          })
        }
        sx={{
          borderRadius: 2.5,
          fontWeight: 700,
          textTransform: 'none',
          py: 1.4,
          fontSize: '0.9rem',
          borderColor: Y_BDR,
          color: Y_DEEP,
          '&:hover': { borderColor: Y, bgcolor: Y_TINT },
        }}
      >
        Create an account
      </Button>
    </Stack>
  </GateShell>
);

// ── Wrong account ─────────────────────────────────────────────────── //
const WrongAccountWall = ({
  userEmail,
  expectedEmail,
  navigate,
  location,
  onLogout,
}) => {
  // Mask expected email for privacy: j***@example.com
  const masked = expectedEmail
    ? (() => {
        const [local, domain] = expectedEmail.split('@');
        if (!domain) return '***@***';
        return `${local.charAt(0)}***@${domain}`;
      })()
    : '(the recipient)';

  return (
    <GateShell
      icon={<PersonOffIcon />}
      title="Wrong account"
      subtitle={`This invoice was sent to ${masked}. You're signed in as ${userEmail}. Switch to the correct account to view it.`}
    >
      <Stack spacing={1.25}>
        <Button
          fullWidth
          variant="contained"
          size="large"
          onClick={onLogout}
          startIcon={<LogoutIcon />}
          sx={{
            bgcolor: Y,
            color: Y_INK,
            fontWeight: 800,
            borderRadius: 2.5,
            textTransform: 'none',
            py: 1.4,
            fontSize: '0.9rem',
            '&:hover': { bgcolor: '#F5C800' },
          }}
        >
          Sign out and switch accounts
        </Button>
        <Alert
          severity="info"
          sx={{ borderRadius: 2, fontSize: '0.78rem' }}
        >
          If you believe this is an error, contact the sender to verify the email address on the invoice.
        </Alert>
      </Stack>
    </GateShell>
  );
};

// ── Main gate component ───────────────────────────────────────────── //
const BBCSplitIdentityGate = ({
  userInfo,
  invoice,
  isCreatorAuthorized,
  onLogout,
  children,
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  // Admins bypass entirely
  if (userInfo?.is_admin) return <>{children}</>;

  // Not logged in
  if (!userInfo) {
    return (
      <LoginWall
        location={location}
        navigate={navigate}
        invoiceRef={invoice?.po_number}
      />
    );
  }

  const clientEmail  = (invoice?.client_email || '').toLowerCase().trim();
  const userEmail    = (userInfo.email || '').toLowerCase().trim();
  const clientUserId = invoice?.client_user_id || invoice?.client_id;

  // Logged-in creator viewing their own assigned invoice is fine
  if (isCreatorAuthorized) return <>{children}</>;

  // Match by user_id if available, fall back to email
  const idMatch    = clientUserId && String(clientUserId) === String(userInfo.id);
  const emailMatch = clientEmail && clientEmail === userEmail;

  if (!idMatch && !emailMatch && clientEmail) {
    return (
      <WrongAccountWall
        userEmail={userInfo.email}
        expectedEmail={invoice?.client_email}
        navigate={navigate}
        location={location}
        onLogout={onLogout}
      />
    );
  }

  // Verified — render invoice
  return <>{children}</>;
};

export default BBCSplitIdentityGate;